/**
 * Auth Utility Functions
 * 
 * Helper functions for authentication operations and user data management.
 */

/**
 * Gets the user display name from Firebase User object
 * 
 * @param {import('firebase/auth').User | null} user - Firebase User object
 * @returns {string | null} User display name or null
 */
export const getUserDisplayName = (user) => {
  if (!user) return null;
  return user.displayName || null;
};

/**
 * Gets the user email from Firebase User object
 * 
 * @param {import('firebase/auth').User | null} user - Firebase User object
 * @returns {string | null} User email or null
 */
export const getUserEmail = (user) => {
  if (!user) return null;
  return user.email || null;
};

/**
 * Syncs Firebase User data to localStorage for backward compatibility
 * 
 * @param {import('firebase/auth').User | null} user - Firebase User object
 */
export const syncUserToLocalStorage = (user) => {
  if (!user) {
    // Clear localStorage auth data if no user
    localStorage.removeItem('codee.user.registered');
    localStorage.removeItem('codee.user.email');
    localStorage.removeItem('codee.user.fullName');
    return;
  }

  // Sync user data to localStorage for backward compatibility
  localStorage.setItem('codee.user.registered', 'true');
  
  if (user.email) {
    localStorage.setItem('codee.user.email', user.email);
  }
  
  if (user.displayName) {
    localStorage.setItem('codee.user.fullName', user.displayName);
  }
};

/**
 * Maps Firebase error codes to user-friendly error messages
 * 
 * @param {Error} error - Firebase error object
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (error) => {
  if (!error || !error.code) {
    return "An error occurred. Please try again";
  }

  const errorCode = error.code;

  // Sign in errors
  if (errorCode === 'auth/user-not-found') {
    return "No account found with this email";
  }
  if (errorCode === 'auth/wrong-password') {
    return "Incorrect password";
  }
  if (errorCode === 'auth/invalid-email') {
    return "Invalid email address";
  }
  if (errorCode === 'auth/too-many-requests') {
    return "Too many attempts. Please try again later";
  }
  if (errorCode === 'auth/network-request-failed') {
    return "Network error. Please check your connection";
  }

  // Sign up errors
  if (errorCode === 'auth/email-already-in-use') {
    return "An account with this email already exists";
  }
  if (errorCode === 'auth/weak-password') {
    return "Password should be at least 6 characters";
  }

  // Password reset errors
  if (errorCode === 'auth/user-not-found') {
    return "No account found with this email";
  }

  // Default error message
  return error.message || "An error occurred. Please try again";
};